import { useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import { useCurrencyState } from "../../../hooks";
import formatVariantPrice from "../lib/format-variant-price";
import { resolveSizes } from "../../ProductCard/lib/resolve-sizes";
import { resolveSale } from "../../ProductCard/lib/resolve-sale";
import { resolveSpecialText } from "../../ProductCard/lib/resolve-special-text";
import { useQuickAdd } from "./hooks/useQuickAdd";
import styles from "./styles/HoverCard.module.css";

const formatTitle = (str) => {
  const wordsWithApostrophes = /^([\w]+['’][\w]+)\s/g;
  return str.replace(wordsWithApostrophes, "");
};

export const HoverCard = ({ hoverState, product, productColour, link, listItemRect }) => {
  const ref = useRef();
  const [cardPosition, setCardPosition] = useState();
  const { currency } = useCurrencyState();
  const { t } = useTranslation("productcard");

  const { selectedColour, selectedSize, setSelectedSize, cartIsLoading } = useQuickAdd(
    product,
    productColour
  );

  useEffect(() => {
    const cardRect = ref.current.getBoundingClientRect();

    const positionStyles = {
      top: listItemRect.bottom - cardRect.height,
      left: -cardRect.width,
    };

    setCardPosition(positionStyles);
  }, [listItemRect]);

  const { title, handle, images, sanity, variants } = product;
  const { price, compareAtPrice } = variants && formatVariantPrice(variants[0], currency);
  const coloursAmount = sanity?.shopifyProducts.length;
  const sizes = resolveSizes(variants);
  const onSale = resolveSale(price, currency);
  const specialText = resolveSpecialText(product, onSale !== null, t);

  const cardStyles = `
    ${styles.card}
    ${hoverState ? styles.cardIsVisible : ""}
  `;

  const Image = () => {
    return (
      <a href={link} className={styles.imgContainer}>
        <img src={images[0].card} alt={images[0].altText} />
      </a>
    );
  };

  const Header = () => {
    return (
      <a href={link}>
        {specialText && (
          <div className={styles.specialText}>
            {specialText.map((text) => (
              <p key={text}>{text}</p>
            ))}
          </div>
        )}
        <p className={styles.title}>{formatTitle(title)}</p>

        <section className={styles.details}>
          <p>{price}</p>
          {compareAtPrice && <p className={styles.lineThrough}>{compareAtPrice}</p>}
          <p>
            {coloursAmount} {coloursAmount === 1 ? "colour" : "colours"}
          </p>
          <p>View</p>
        </section>
      </a>
    );
  };

  const Sizes = () => {
    return (
      <section>
        <p className={styles.subtitle}>Quick Add</p>
        {cartIsLoading && selectedColour.handle === handle && (
          <p className={styles.addMessage}>Adding {selectedSize} to bag</p>
        )}

        {!cartIsLoading && (
          <div className={styles.sizes}>
            <p>Size</p>
            <ul className={styles.sizeList}>
              <SizeButtons />
            </ul>
          </div>
        )}
      </section>
    );
  };

  const SizeButtons = () => {
    return sizes.map((item) => {
      const { size, available } = item;

      const sizeBtnStyles = `
        ${styles.sizeBtn}
        ${!available ? styles.sizeBtnDisabled : ""}
      `;

      return (
        <li key={size}>
          {available && (
            <button
              className={sizeBtnStyles}
              disabled={!available}
              onClick={() => setSelectedSize(size)}
            >
              {size}
            </button>
          )}

          {!available && (
            <a href={`${link}&size=${size.toLowerCase()}&waitlist=true`} className={sizeBtnStyles}>
              {size}
            </a>
          )}
        </li>
      );
    });
  };

  return (
    <article ref={ref} className={cardStyles} style={cardPosition}>
      <Image />
      <section className={styles.content}>
        <Header />
        <Sizes />
      </section>
    </article>
  );
};
